import { format } from 'date-fns';
import { ChevronDown, Globe, Info, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MissingPagesPopover } from './MissingPages.popover';
import { RecentAdditionsPopover } from './RecentAdditions.popover';
import { SyncedSiteScopeModal } from './SyncedSiteScope.modal';
import {
  SyncedSiteDto,
  SyncedSiteSuggestionDto,
} from '@/Domain/api/KnowledgeBase/dto/SyncedSite.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/Domain/components/Shared/Collapsible/Collapsible';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useAutoRefreshAvailability } from '@/Domain/hooks/KnowledgeBase/useAutoRefreshAvailability.hook';
import { useSyncedSites } from '@/Domain/hooks/KnowledgeBase/useSyncedSites.hook';
import { cn } from '@/Domain/utils/cn';

type StatusLine = { key: string; params?: Record<string, unknown> };

// Line 1 (daily checks): first match wins. Never mentions page content.
const statusLine = (site: SyncedSiteDto, available: boolean, date: string | null): StatusLine => {
  if (!available) return { key: 'synced_sites.status.unavailable' };
  if (site.sync_running) return { key: 'synced_sites.status.running' };
  if (site.last_sync_status === 'map_failed') return { key: 'synced_sites.status.map_failed' };
  if (!site.sync_enabled) return { key: 'synced_sites.status.off' };
  if (!site.last_synced_at) return { key: 'synced_sites.status.pending_first' };
  if (!site.has_baseline) return { key: 'synced_sites.status.baseline', params: { date } };
  return { key: 'synced_sites.status.last_checked', params: { date } };
};

// Every outcome of the last run, side by side: a check that found 2 pages and
// lost 7 has to say both. Additions land asynchronously, so "importing" and
// "added" are separate states and neither is inferred from the other.
const deltaLines = (site: SyncedSiteDto): StatusLine[] => {
  const parts: StatusLine[] = [];
  if (site.last_imported_count > 0) {
    parts.push({
      key: 'synced_sites.delta.imported',
      params: { count: site.last_imported_count },
    });
  }
  if (site.import_in_flight && site.last_dispatched_count > 0) {
    parts.push({
      key: 'synced_sites.delta.importing',
      params: { count: site.last_dispatched_count },
    });
  }
  if (site.last_failed_count > 0) {
    parts.push({
      key: 'synced_sites.delta.failed',
      params: { count: site.last_failed_count },
    });
  }
  if (site.last_quota_blocked_count > 0) {
    parts.push({
      key: 'synced_sites.delta.waiting',
      params: { count: site.last_quota_blocked_count },
    });
  }
  // Fuse-deferred pages are not plan-blocked: an unlimited plan can reach this
  // state, so it must never be reported as needing more space.
  if (site.last_deferred_count > 0) {
    parts.push({
      key: 'synced_sites.delta.deferred',
      params: { count: site.last_deferred_count },
    });
  }
  if (site.last_missing_count > 0) {
    parts.push({
      key: 'synced_sites.delta.missing',
      params: { count: site.last_missing_count },
    });
  }
  return parts.length > 0 ? parts : [{ key: 'synced_sites.delta.none' }];
};

// Line 2 (auto-refresh coverage): shown only when available && sync_enabled.
const coverageLine = (site: SyncedSiteDto): StatusLine => {
  if (site.watched_count === 0) return { key: 'synced_sites.coverage.none' };
  if (site.watched_count === site.articles_count) {
    return { key: 'synced_sites.coverage.all', params: { count: site.articles_count } };
  }
  return {
    key: 'synced_sites.coverage.partial',
    params: { watched: site.watched_count, total: site.articles_count },
  };
};

type SyncNowState = {
  disabled: boolean;
  running: boolean;
  label: string;
  tooltip: string | null;
};

const syncNowState = (
  site: SyncedSiteDto,
  available: boolean,
  isPending: boolean,
): SyncNowState => {
  if (isPending) {
    return {
      disabled: true,
      running: false,
      label: 'synced_sites.sync_now_pending',
      tooltip: null,
    };
  }
  if (!available) {
    return {
      disabled: true,
      running: false,
      label: 'synced_sites.sync_now',
      tooltip: 'synced_sites.unavailable_tooltip',
    };
  }
  if (!site.sync_enabled) {
    return {
      disabled: true,
      running: false,
      label: 'synced_sites.sync_now',
      tooltip: 'synced_sites.sync_now_disabled.sync_off',
    };
  }
  if (site.sync_running) {
    return {
      disabled: true,
      running: true,
      label: 'synced_sites.sync_now_running',
      tooltip: 'synced_sites.sync_now_disabled.already_running',
    };
  }
  return {
    disabled: false,
    running: false,
    label: 'synced_sites.sync_now',
    tooltip: 'synced_sites.sync_now_tooltip',
  };
};

type SyncedSiteRowProps = {
  site: SyncedSiteDto;
  available: boolean;
  isToggling: boolean;
  isSyncPending: boolean;
  onToggle: (siteId: number, enabled: boolean) => void;
  onRunSync: (site: SyncedSiteDto) => void;
  onEditScope: (site: SyncedSiteDto) => void;
};

const SyncedSiteRow = ({
  site,
  available,
  isToggling,
  isSyncPending,
  onToggle,
  onRunSync,
  onEditScope,
}: SyncedSiteRowProps) => {
  const { t } = useTranslation('knowledge_base');
  const lastSynced = site.last_synced_at ? format(new Date(site.last_synced_at), 'PP') : null;

  const status = statusLine(site, available, lastSynced);
  const deltas = status.key === 'synced_sites.status.last_checked' ? deltaLines(site) : [];
  const showCoverage = available && site.sync_enabled;
  const coverage = coverageLine(site);
  const sync = syncNowState(site, available, isSyncPending);

  const switchEl = (
    <span className={cn('inline-flex', isToggling && 'cursor-wait')}>
      <Switch
        checked={site.sync_enabled}
        onCheckedChange={(next) => onToggle(site.id, next)}
        disabled={!available || isToggling}
        className={cn(isToggling && 'pointer-events-none')}
        aria-label={t('synced_sites.toggle_aria')}
        data-testid={`synced-site-switch-${site.id}`}
      />
    </span>
  );

  const checkNowButton = (
    <Button
      variant="secondary"
      size="xs"
      onClick={() => onRunSync(site)}
      disabled={sync.disabled}
      loading={sync.running}
      aria-label={t('synced_sites.sync_now_aria')}
      data-testid={`synced-site-sync-${site.id}`}
    >
      {!sync.running && <RefreshCw className="size-3.5" />}
      {t(sync.label)}
    </Button>
  );

  return (
    <li
      className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid={`synced-site-row-${site.id}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <Globe className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 space-y-1">
          <p className="truncate text-sm font-medium text-foreground" title={site.root_url}>
            {site.root_url}
          </p>
          <p
            className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground"
            data-testid={`synced-site-status-${site.id}`}
          >
            <span
              className={cn(
                site.last_sync_status === 'map_failed' &&
                  !site.sync_running &&
                  'text-warning-foreground',
              )}
            >
              {t(status.key, status.params)}
            </span>
            {deltas.length > 0 && (
              <span data-testid={`synced-site-delta-${site.id}`}>
                {deltas.map((part) => t(part.key, part.params)).join(' · ')}
              </span>
            )}
          </p>
          {showCoverage && (
            <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-foreground-subtle">
              <span data-testid={`synced-site-coverage-${site.id}`}>
                {t(coverage.key, coverage.params)}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label={t('synced_sites.coverage.manage')}
                  >
                    <Info className="size-3 text-foreground-subtle" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>{t('synced_sites.coverage.tooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 self-end sm:self-center">
        <MissingPagesPopover siteId={site.id} missingCount={site.last_missing_count} />
        <RecentAdditionsPopover siteId={site.id} />
        {site.sync_running ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  variant="ghost"
                  size="xs"
                  disabled
                  data-testid={`synced-site-edit-scope-${site.id}`}
                >
                  <SlidersHorizontal className="size-3.5" />
                  {t('synced_sites.actions.edit_scope')}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('synced_sites.scope.entry_disabled_tooltip')}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => onEditScope(site)}
            data-testid={`synced-site-edit-scope-${site.id}`}
          >
            <SlidersHorizontal className="size-3.5" />
            {t('synced_sites.actions.edit_scope')}
          </Button>
        )}
        {sync.tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">{checkNowButton}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t(sync.tooltip)}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          checkNowButton
        )}
        {available ? (
          switchEl
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>{switchEl}</TooltipTrigger>
            <TooltipContent>
              <p>{t('synced_sites.unavailable_tooltip')}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </li>
  );
};

type SuggestionRowProps = {
  suggestion: SyncedSiteSuggestionDto;
  available: boolean;
  isStarting: boolean;
  disabled: boolean;
  onKeepInSync: (rootUrl: string) => void;
};

const SuggestionRow = ({
  suggestion,
  available,
  isStarting,
  disabled,
  onKeepInSync,
}: SuggestionRowProps) => {
  const { t } = useTranslation('knowledge_base');
  const button = (
    <Button
      variant="secondary"
      size="xs"
      onClick={() => onKeepInSync(suggestion.root_url)}
      disabled={!available || disabled}
      loading={isStarting}
      data-testid={`synced-site-suggestion-keep-${suggestion.root_url}`}
    >
      {t('synced_sites.suggestions.keep_in_sync')}
    </Button>
  );
  return (
    <li
      className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid={`synced-site-suggestion-${suggestion.root_url}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <Globe className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 space-y-1">
          <p className="truncate text-sm font-medium text-foreground" title={suggestion.root_url}>
            {suggestion.root_url}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('synced_sites.suggestions.pages', { count: suggestion.articles_count })}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center self-end sm:self-center">
        {available ? (
          button
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">{button}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('synced_sites.unavailable_tooltip')}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </li>
  );
};

export const SyncedSitesPanel = () => {
  const { t } = useTranslation('knowledge_base');
  const {
    sites,
    suggestions,
    toggle,
    togglingIds,
    runSync,
    syncingIds,
    keepInSync,
    startingRootUrl,
  } = useSyncedSites();
  const { available } = useAutoRefreshAvailability();
  const [open, setOpen] = useState(true);
  const [scopeSite, setScopeSite] = useState<SyncedSiteDto | null>(null);

  // Snapshot last_synced_at at the moment the user triggers a run, so completion
  // can tell "nothing changed" from "the run never got to do anything".
  const runSnapshotRef = useRef<Map<number, string | null>>(new Map());
  const prevRunningRef = useRef<Map<number, boolean>>(new Map());

  const handleRunSync = useCallback(
    (site: SyncedSiteDto) => {
      runSnapshotRef.current.set(site.id, site.last_synced_at);
      runSync(site.id);
    },
    [runSync],
  );

  // Report the outcome once a user-triggered run finishes. The row keeps showing
  // the delta; this is the moment-of-completion notice, and it reports what the
  // run actually did: pages handed to the scraper are "importing", not "added",
  // pages left for a later run are named, missing pages are named as kept, and a
  // zero result is a result rather than proof nothing was eligible.
  useEffect(() => {
    const prev = prevRunningRef.current;
    sites.forEach((site) => {
      const wasRunning = prev.get(site.id) ?? false;
      if (wasRunning && !site.sync_running && runSnapshotRef.current.has(site.id)) {
        const startedAt = runSnapshotRef.current.get(site.id) ?? null;
        runSnapshotRef.current.delete(site.id);
        const date = site.last_synced_at ? format(new Date(site.last_synced_at), 'PP') : '';
        if (site.last_sync_status === 'map_failed') {
          toast(t('synced_sites.sync_result.map_failed'));
        } else if (site.last_synced_at === startedAt) {
          toast(t('synced_sites.sync_result.did_not_run'));
        } else {
          const parts: string[] = [];
          if (site.last_dispatched_count > 0) {
            parts.push(
              t('synced_sites.sync_result.importing', {
                count: site.last_dispatched_count,
                date,
              }),
            );
          }
          if (site.last_failed_count > 0) {
            parts.push(t('synced_sites.sync_result.failed', { count: site.last_failed_count }));
          }
          if (site.last_quota_blocked_count > 0) {
            parts.push(
              t('synced_sites.sync_result.waiting', { count: site.last_quota_blocked_count }),
            );
          }
          if (site.last_deferred_count > 0) {
            parts.push(t('synced_sites.sync_result.deferred', { count: site.last_deferred_count }));
          }
          if (site.last_missing_count > 0) {
            parts.push(t('synced_sites.sync_result.missing', { count: site.last_missing_count }));
          }
          toast(
            parts.length > 0 ? parts.join(' ') : t('synced_sites.sync_result.no_changes', { date }),
          );
        }
      }
      prev.set(site.id, site.sync_running);
    });
  }, [sites, t]);

  if (sites.length === 0 && suggestions.length === 0) {
    return null;
  }

  return (
    <TooltipProvider>
      <section className="rounded-lg border border-border bg-card" data-testid="synced-sites-panel">
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-4 py-3 text-left outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring/50"
              data-testid="synced-sites-panel-toggle"
            >
              <span className="flex flex-col gap-0.5">
                <span className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {t('synced_sites.title')}
                  </span>
                  {sites.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {t('synced_sites.sites_count', { count: sites.length })}
                    </span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">{t('synced_sites.subtitle')}</span>
              </span>
              <ChevronDown
                className={cn(
                  'size-4 shrink-0 text-muted-foreground transition-transform',
                  !open && '-rotate-90',
                )}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex flex-col gap-1 border-t border-border px-4 py-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">
                {t('synced_sites.explainer.title')}
              </span>
              <span>{t('synced_sites.explainer.does')}</span>
              <span>{t('synced_sites.explainer.does_not')}</span>
            </div>
            {sites.length > 0 && (
              <ul className="divide-y divide-border border-t border-border">
                {sites.map((site) => (
                  <SyncedSiteRow
                    key={site.id}
                    site={site}
                    available={available}
                    isToggling={togglingIds.has(site.id)}
                    isSyncPending={syncingIds.has(site.id)}
                    onToggle={toggle}
                    onRunSync={handleRunSync}
                    onEditScope={setScopeSite}
                  />
                ))}
              </ul>
            )}
            {suggestions.length > 0 && (
              <div className="border-t border-border" data-testid="synced-sites-suggestions">
                <div className="flex flex-col gap-0.5 px-4 pt-3 text-xs">
                  <span className="font-medium text-foreground">
                    {t('synced_sites.suggestions.title')}
                  </span>
                  <span className="text-muted-foreground">
                    {t('synced_sites.suggestions.description')}
                  </span>
                </div>
                <ul className="divide-y divide-border">
                  {suggestions.map((suggestion) => (
                    <SuggestionRow
                      key={suggestion.root_url}
                      suggestion={suggestion}
                      available={available}
                      isStarting={startingRootUrl === suggestion.root_url}
                      disabled={startingRootUrl !== null}
                      onKeepInSync={keepInSync}
                    />
                  ))}
                </ul>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </section>

      {scopeSite && (
        <SyncedSiteScopeModal
          key={scopeSite.id}
          siteId={scopeSite.id}
          rootUrl={scopeSite.root_url}
          open={!!scopeSite}
          onOpenChange={(next) => !next && setScopeSite(null)}
          onRunCheck={(siteId) => runSync(siteId)}
        />
      )}
    </TooltipProvider>
  );
};
